/** @format */

import React, { FunctionComponent, ReactElement, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import GridContainer from '../../../../../components/Grid/GridContainer';
import GridItem from '../../../../../components/Grid/GridItem';
import {
    SimCardServiceItemConfiguration,
    SimCardServiceTypeConfigurationGet,
    SimCardServiceTypeConfigurationPost,
    SimCardServiceTypeConfigurationPut,
    SimCardServiceTypeItem,
} from '../../../../../interfaces/collections/SimCardServiceInterface';
import I18n from '../../../../../components/Translate/I18n';
import CustomTextButtom from '../../../../../components/Buttons/CustomTextButton';
import { SimpleSelect } from '../../../../../components/Adescom/SimpleSelect';
import { getBooleanElementList } from '../../../../lib/search/listTableHeader/HeaderBooleanCheckBoxSelectList';
import {
    PACKAGE_ACTIVATE,
    PACKAGE_EXPIRE,
    PACKAGE_NEAR_EXPIRE,
    PACKAGE_USAGE_80_PERCENT,
    PACKAGE_USAGE_ALL,
    SERVICE_DAP_BCN_APPROACHING_ZERO,
    SERVICE_DAP_BCN_ZERO,
} from '../../../../../modules/features/SmsRequestTemplateType';
import { ItemLoaders } from '../../../../../core/loaders/ItemLoaders';
import { useDispatch } from 'react-redux';
import { FormGeneralHelper } from '../../../../../helpers/FormGeneral';
import CustomInputExt from '../../../../../components/CustomInput/CustomInputExt';
import { Auth } from '../../../../../modules/auth/Auth';

type OplSimCardServiceTypeNaiSmsMsgEditType = {
    open: boolean;
    reload: (data: SimCardServiceTypeConfigurationGet) => void;
    simCardServiceType: SimCardServiceTypeItem;
    close: () => void;
    languageName: string;
    simCardServiceTypeConfiguration: SimCardServiceTypeConfigurationGet | SimCardServiceTypeConfigurationPost | SimCardServiceTypeConfigurationPut;
    isPost: boolean;
    id?: string | null;
    languageId?: number | null;
    platformResellerId: number;
};

export const OplSimCardServiceTypeNaiSmsMsgEdit: FunctionComponent<OplSimCardServiceTypeNaiSmsMsgEditType> = ({
    open,
    reload,
    simCardServiceType,
    close,
    simCardServiceTypeConfiguration,
    languageName,
    isPost,
    id,
    languageId,
    platformResellerId,
}): ReactElement => {
    const dispatch = useDispatch();
    const [configuration, setConfiguration] = useState<SimCardServiceItemConfiguration>(JSON.parse(simCardServiceTypeConfiguration.configuration));
    const save = (): void => {
        simCardServiceTypeConfiguration.configuration = JSON.stringify(configuration);
        const itemLoader = new ItemLoaders(dispatch);
        if (!isPost) {
            itemLoader.putWithCallback(
                FormGeneralHelper.getByUri(id ?? 'empty'),
                'no-dispatch',
                simCardServiceTypeConfiguration,
                (data: SimCardServiceTypeConfigurationGet): void => {
                    reload(data);
                    close();
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        } else {
            const simCardServiceTypeConfigurationPost: SimCardServiceTypeConfigurationPost = {
                configuration: simCardServiceTypeConfiguration.configuration,
                simCardServiceType: '/base-api/api/platform/sim_card_service_types/' + simCardServiceType.id.toString(),
                systemLanguage: '/base-api/api/platform/system_languages/' + (languageId ?? -1).toString(),
                platformReseller: '/base-api/api/platform/platform_resellers/' + platformResellerId.toString(),
            };
            itemLoader.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_type_configurations')),
                'no-dispatch',
                simCardServiceTypeConfigurationPost,
                (data: SimCardServiceTypeConfigurationGet): void => {
                    reload(data);
                    close();
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    };

    const onChangeSelectEvent = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const field = event.target.name;
        const selected = parseInt(event.target.value);
        setConfiguration({ ...configuration, [field]: selected === 1 });
    };

    const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        const field = event.target.name;
        setConfiguration({ ...configuration, [field]: value });
    };

    return (
        <Dialog open={open} maxWidth="md" onClose={close} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle>
                <I18n trans="prov:feature.sms_message_configure_with_language" valueObject={{ language: languageName.toUpperCase() }} />
            </DialogTitle>
            <DialogContent>
                <GridContainer>
                    <GridItem xs={12} style={{ paddingTop: '20px' }}>
                        <I18n trans="prov:feature.package_has_got_zero" />
                    </GridItem>
                    <GridItem xs={12} style={{ paddingTop: '20px' }}>
                        <SimpleSelect
                            label="prov:feature.package_has_got_zero"
                            fieldName={SERVICE_DAP_BCN_ZERO}
                            required={true}
                            elements={getBooleanElementList()}
                            dispatchName={'SMS_MESSAGE'}
                            selectedValue={configuration.SERVICE_DAP_BCN_ZERO ? 1 : 2}
                            callback={onChangeSelectEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <CustomInputExt
                            formControlProps={{ fullWidth: true }}
                            required={true}
                            labelText={'prov:feature.package_sms'}
                            name="SERVICE_DAP_BCN_ZERO_MSG"
                            value={configuration.SERVICE_DAP_BCN_ZERO_MSG ?? ''}
                            onChange={onChangeEvent}
                        />
                    </GridItem>
                    <GridItem xs={12} style={{ paddingTop: '20px' }}>
                        <SimpleSelect
                            label="prov:feature.package_is_approaching_zero"
                            fieldName={SERVICE_DAP_BCN_APPROACHING_ZERO}
                            required={true}
                            elements={getBooleanElementList()}
                            dispatchName={'SMS_MESSAGE'}
                            selectedValue={configuration.SERVICE_DAP_BCN_APPROACHING_ZERO ? 1 : 2}
                            callback={onChangeSelectEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <CustomInputExt
                            formControlProps={{ fullWidth: true }}
                            required={true}
                            labelText={'prov:feature.package_sms'}
                            name="SERVICE_DAP_BCN_APPROACHING_ZERO_MSG"
                            value={configuration.SERVICE_DAP_BCN_APPROACHING_ZERO_MSG ?? ''}
                            onChange={onChangeEvent}
                        />
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom simple callback={close} callbackParam={{}} label="general:common.close" size="sm" />
                <CustomTextButtom callback={save} callbackParam={{}} label="prov:feature.confirm_title" size="sm" />
            </DialogActions>
        </Dialog>
    );
};
